import { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import {
  AlertTriangle,
  Bug,
  ChevronDown,
  ExternalLink,
  Github,
  Home,
  Lock,
  MonitorPlay,
  Pencil,
  Tv,
} from 'lucide-react';
import { Link } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import type { GameStatusBadge } from '@volley/crucible-ipc-types/ipc-channels';
import { unflattenIpcError } from '@volley/crucible-ipc-types/error-types';
import { useIpcQuery } from '../hooks/useIpc';
import { isSupportedTileUploadFile, prepareTileImageUpload } from '../tile-image-upload';
import { PHASE_STYLE } from './GameStatusPanel';
import { FoundryShellHeader } from './FoundryShellHeader';
import { Tooltip } from './ui/tooltip';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { AgentActivityPill } from './AgentActivityPill';
import { VmStatusPill } from './VmStatusPill';
import { FurnaceBuildIndicator } from './FurnaceBuildIndicator';
import { useFeatureFlags } from '../hooks/useFeatureFlags';
import { useAppFrame } from '../AppFrameContext';
import {
  deriveDeployActionState,
  type DeployActionState,
  type DeployState,
} from '../workspace/deploy-action-state';
import { derivePreviewLaunchPillState } from '../workspace/plan-build-handoff';
import type { FurnaceBuildState } from '../workspace/furnace-build-state';
import type { WorkspaceRunState } from '../workspace/run-state';
import type {
  CloneDeployAccessWarning,
  WorkspaceHeaderMode,
} from '../workspace/workspace-view-types';
import { useCrucibleClient } from '../transport/context';
import { projectQueryKey } from '../transport/project-query-key';
import { buildGameBugReportUrl } from '../game-bug-report';

// ---------------------------------------------------------------------------
// Header
// ---------------------------------------------------------------------------

// Maps deploy state to Badge variant (semantic token intents via design-system §5).
// Previously held raw className strings; now delegates to Badge so intent tokens
// are consistent with the rest of the design system.
const DEPLOY_BADGE_VARIANT: Record<
  DeployState,
  { label: string; variant: 'default' | 'success' | 'warning' | 'error' | 'info' }
> = {
  idle: { label: 'Draft', variant: 'default' },
  starting: { label: 'Starting...', variant: 'info' },
  running: { label: 'Running', variant: 'success' },
  deploying: { label: 'Deploying...', variant: 'warning' },
  deployed: { label: 'Deployed', variant: 'success' },
  error: { label: 'Error', variant: 'error' },
};

/**
 * Exported for the deploy-failure-pill test (#408). The header is
 * otherwise an internal Workspace concern; test export does NOT
 * widen the public surface — the only consumer is `Workspace.tsx`
 * itself plus the unit test for the failed-step chip + deep-link.
 */
export function WorkspaceHeader({
  gameId,
  gameName,
  deployState,
  deployActionState,
  selectionAddedHint,
  gameStatus,
  cloneDeployAccessWarning,
  deployFailureContext,
  localFailedCheck,
  activitySnapshot,
  interviewStatus,
  onChat,
  onDesign,
  designDisabledReason,
  onArt,
  artDisabledReason,
  onPreviewTab,
  editorDisabledReason,
  renameDisabledReason,
  tileEditDisabledReason,
  onLivePreview,
  onProtoHub,
  livePreviewSplitActive = false,
  previewLoading,
  previewMenuEnabled,
  previewForceDisabled = false,
  previewDisabledReason,
  previewAvailableTitle,
  inAppPairingAvailable = false,
  inAppPairingNextLaunch = false,
  onInAppPairingNextLaunchChange,
  statusBadge = 'draft',
  repoUrl,
  previewPhaseGate,
  onDeploy,
  showBackToPlanButton,
  backToPlanBusy,
  onBackToPlanRequest,
  activeMode = 'chat',
  contributors,
  rightPanelOpen,
  onToggleRightPanel,
  runState,
  cloudVmEnabled = false,
  maintainerMode = false,
  furnaceBuildState = {
    phase: 'inactive',
    routedToFurnace: false,
    label: '',
  },
}: {
  gameId: string;
  gameName: string;
  deployState: DeployState;
  deployActionState?: DeployActionState;
  /** Select-to-chat — pulse the Build tab + show a transient "added to
   *  Build" popover beneath it (true for ~5s after a selection lands). */
  selectionAddedHint?: boolean;
  gameStatus?: import('@volley/crucible-ipc-types/ipc-channels').GameStatus;
  /** Game lifecycle badge for the project settings panel (Draft/Live/…). */
  statusBadge?: GameStatusBadge;
  /** GitHub repo URL for the project panel's Repo button (cloned games only). */
  repoUrl?: string;
  cloneDeployAccessWarning?: CloneDeployAccessWarning;
  /**
   * Issue #791 — live agent-activity snapshot from
   * `useActivitySnapshot(gameId)`. Drives the new agent-activity
   * pill's three-mode derivation (Ready / Running / Waiting on you).
   * Optional + defaults to `null` so existing call sites (including
   * the `WorkspaceHeader` unit tests that pre-date this prop) keep
   * working — the pill folds to `Ready` when both surfaces are
   * absent.
   */
  activitySnapshot?: import('@volley/crucible-ipc-types/ipc-channels').ActivitySnapshot | null;
  /**
   * Issue #791 — `build-game:interview-status` summary status. Covers
   * the pre-snapshot-registration window at the start of a build
   * where the SKILL has written `status: 'in-progress'` to disk but
   * no `activity:state` push has landed yet. Without this the pill
   * would briefly read `Ready` while a question is already on the
   * screen. Optional + defaults to `null` for the same back-compat
   * reasoning as `activitySnapshot`.
   */
  interviewStatus?:
    | import('@volley/crucible-ipc-types/build-game-types').BuildGameInterviewStatus
    | null
    | undefined;
  /**
   * User-facing aggregate run state. When present this is the single
   * source for the header activity pill copy/tone.
   */
  runState?: WorkspaceRunState | null;
  /**
   * Issue #408 — when the deploy widget is in `error` state AND we
   * have a failure context for the latest run, pull the failed
   * step name + deep-link to the failed job page so the pill can
   * answer "what broke?" without the user opening the card. Always
   * undefined unless `deployState === 'error'`.
   */
  deployFailureContext?: import('@volley/crucible-ipc-types/ipc-channels').DeployFailureContext;
  /**
   * Issue #415 Layer 3 — when a LOCAL pre-flight quality-gate
   * check fails (lint / typecheck / shared build), the renderer
   * has no `deployFailureContext` to pull a failed-step name from
   * (no GitHub workflow run exists yet). Main pushes the failing
   * check name on `publish:progress.failedCheck`, the renderer
   * stores it in `localFailedCheck`, and the same "Failed step"
   * chip from #408/#413 renders it. Same UX, different source —
   * exactly the spec's "use the SAME UX as the existing deploy-
   * failure flow" requirement.
   */
  localFailedCheck?: string | null;
  onChat?: () => void;
  /** Enter Design mode. Only passed when the process flag and user setting
   *  both allow the Design workspace. */
  onDesign?: () => void;
  designDisabledReason?: string;
  /** Open the Art tab. Only passed when `CRUCIBLE_ART_TAB=1` and a game is
   *  loaded (crucible#2611 Task 4). */
  onArt?: () => void;
  artDisabledReason?: string;
  /**
   * #4053 — open the Preview tab. ALWAYS clickable, unlike `onDesign`/
   * `onArt`: when preview is blocked (no game / phase-gated / force-
   * disabled), the click still fires — the blocked-pane content answers
   * "why" instead of the tab silently no-op'ing. Replaced the icon-only
   * Preview button that used to live in the top-right action cluster
   * (removed — see the mode-pill bar).
   */
  onPreviewTab?: () => void;
  editorDisabledReason?: string;
  /**
   * #3144 — when set, the game-title rename affordance in the project
   * settings panel is gated: cloud placement has no `game.set-display-name@1`
   * handler, so the invoke would reject with a bare `-32601`. The reason is
   * surfaced via the rename error span and the invoke is never attempted.
   * Mirrors `editorDisabledReason` (Open-in-Cursor's capability gate).
   */
  renameDisabledReason?: string;
  /**
   * #3145 — when set, the tile-image upload dropzone in the project settings
   * panel is gated: cloud placement has no `game.set-tile-image@1` handler.
   * The reason is surfaced via the tile-image error span (a human message,
   * not the raw wire error) and the invoke is never attempted.
   */
  tileEditDisabledReason?: string;
  /**
   * Toggle the live-preview split (#1408/#1420 — preview docked above
   * the chat). Only passed when live preview is enabled in the new
   * UI, so flag-OFF headers render identical chrome. Deliberately
   * WEAKER-gated than the Preview pill: the pill toggles a surface
   * (whose embedded overlay shows its own loading/launch states), so
   * it disables only on no-game / hard force-disable — never on
   * `previewLoading` or the phase gate (see the pill render site).
   */
  onLivePreview?: () => void;
  /** Open the Mac-app-only ProtoHub TV simulator. */
  onProtoHub?: () => void;
  /** Whether the live-preview split is currently showing (pill active state). */
  livePreviewSplitActive?: boolean;
  previewLoading: boolean;
  previewMenuEnabled: boolean;
  /** Additional parent-owned gate for the Preview pill. */
  previewForceDisabled?: boolean;
  /** User-facing title copy when the Preview pill is disabled by parent state. */
  previewDisabledReason?: string | undefined;
  /** User-facing title copy when Preview is enabled. */
  previewAvailableTitle?: string | undefined;
  inAppPairingAvailable?: boolean;
  inAppPairingNextLaunch?: boolean;
  onInAppPairingNextLaunchChange?: (next: boolean) => void;
  previewPhaseGate?: { currentPhase: number; phaseLabel: string } | undefined;
  onDeploy?: () => void;
  showBackToPlanButton?: boolean;
  backToPlanBusy?: boolean;
  onBackToPlanRequest?: () => void;
  /** Which mode pill is currently active. Defaults to 'chat'. */
  activeMode?: WorkspaceHeaderMode;
  /** GitHub contributors for the current game — shown as a facepile in the new UI. */
  contributors?: { login: string; avatarUrl: string }[];
  rightPanelOpen?: boolean;
  onToggleRightPanel?: () => void;
  /**
   * Cloud-VM supportability TDD §A-1 — gates the `<VmStatusPill />`
   * mount. The pill calls no hooks that need a provider; gating here
   * keeps SSR tests of `WorkspaceHeader` free of `useFeatureFlags`'s
   * TanStack Query dependency. Defaults to `false` so existing test
   * callers that pre-date the pill keep rendering identical chrome.
   */
  cloudVmEnabled?: boolean;
  /**
   * Cloud-VM supportability TDD §B-1 — true when Foundry was launched
   * with `CRUCIBLE_MAINTAINER_MODE=1`. Mirrors the `cloudVmEnabled`
   * pattern: the parent reads the gate hook (`useMaintainerMode`)
   * once and threads the boolean through here so the header stays
   * SSR-friendly for tests that don't wrap in a QueryClientProvider.
   * Currently informational only (no in-header surface flips on this
   * yet); reserved so a future maintainer-mode indicator pill can
   * be added without changing the call sites. Defaults to false so
   * existing test callers that pre-date the prop keep rendering
   * identical chrome.
   */
  maintainerMode?: boolean;
  /**
   * Bridge-3 UX — derived Furnace build state from
   * `computeFurnaceBuildState`. The parent owns the inputs
   * (`FurnaceStatus` query + chat reducer's `isAiTyping` + VM
   * connection subscription + failure latch) so the header stays
   * SSR-friendly for tests that render it without a
   * QueryClientProvider. Defaults to a permanently-inactive value
   * (routedToFurnace=false) when omitted so existing test callers
   * that pre-date the prop render identical chrome.
   */
  furnaceBuildState?: FurnaceBuildState;
}): React.JSX.Element {
  const client = useCrucibleClient();
  const { newUi, tileImageUpload } = useFeatureFlags();
  const frame = useAppFrame();
  const shellChrome = frame === 'embedded' ? 'web' : 'electron';
  // Suppress the unused-prop lint while the maintainer-mode header
  // surface is reserved-but-unused. Removing the void once a
  // consumer mounts off the flag.
  void maintainerMode;
  const deployUiState =
    deployActionState ??
    deriveDeployActionState({
      deployState,
      deployFailureContext,
      localFailedCheck,
      ciTriggeredThisDeploy: false,
      hasAnthropicKey: true,
    });
  // Project settings panel — opened by clicking the game title (new UI).
  const [projectPanelOpen, setProjectPanelOpen] = useState(false);
  const titleBtnRef = useRef<HTMLButtonElement>(null);
  const deployBadge = DEPLOY_BADGE_VARIANT[deployState];

  const gitBranch = gameStatus?.localGit?.branch;
  const bifrostPhase = gameStatus?.bifrost?.phase;
  const registryVersion = gameStatus?.registry?.version;
  const projectPlacement = gameStatus?.placement;
  const projectPlacementLabel = projectPlacement === 'cloud' ? 'Cloud' : 'This Mac';
  const repoFullName = gameStatus?.githubRepository;
  const { data: foundryVersion } = useIpcQuery('app:get-version', undefined, {
    enabled: Boolean(repoFullName),
    staleTime: Infinity,
  });
  const gameBugReport = buildGameBugReportUrl({
    repoFullName: repoFullName ?? '',
    gameId,
    gameName,
    foundryVersion: foundryVersion === 'browser-spa-dev' ? undefined : foundryVersion,
    gameVersion: registryVersion,
    branch: gitBranch,
    placement: projectPlacement,
    surface: shellChrome === 'electron' ? 'desktop' : 'cloud',
    workspaceMode: activeMode,
    deployState,
  });
  const reportBugTooltip = gameBugReport
    ? `Report a bug for ${gameName}`
    : 'No GitHub origin is configured for this game';

  // Issue #408 — pick the first failed job's step name + deep-link
  // off the failure context. We surface BOTH next to the pill (a
  // separate "Failed step:" chip and a clickable arrow icon) rather
  // than swallowing the existing pill — the pill colour is the
  // load-bearing visual cue, the chip is the additive context.
  //
  // Issue #415 Layer 3 — when no remote failure context exists
  // (i.e. the failure was a LOCAL pre-flight quality-gate check
  // and CI never ran), fall back to `localFailedCheck` so the
  // chip still surfaces the cause. This is the SAME chip the
  // #408/#413 work introduced; we just changed the source.
  const firstFailedJob = deployFailureContext?.failedJobs?.[0];
  const failedStepName = firstFailedJob?.failedStep ?? localFailedCheck ?? undefined;
  const failedRunUrl =
    deployFailureContext?.failedRunUrl ??
    firstFailedJob?.htmlUrl ??
    deployFailureContext?.workflowRun?.htmlUrl;

  // #4053 — shared by the Preview mode-pill tab (always) and the icon-only
  // Preview button (until AC4 removes it). Computed once so the two
  // consumers can't drift on what "blocked" means.
  const previewPill = derivePreviewLaunchPillState({
    gameId,
    previewLoading,
    previewForceDisabled,
    previewDisabledReason,
    previewAvailableTitle,
    previewPhaseGate,
  });

  const handleOpenInCursor = (): void => {
    if (editorDisabledReason) return;
    void client
      .invoke('game:open-in-editor', {
        placement: gameStatus?.placement ?? 'local',
        gameId,
      })
      .catch((err) => console.warn('[game:open-in-editor] failed:', err));
  };

  const handleReportBug = (): void => {
    if (!gameBugReport) return;
    window.open(gameBugReport.url, '_blank', 'noopener,noreferrer');
  };

  if (!newUi) {
    return (
      <FoundryShellHeader
        chrome={shellChrome}
        leadingClassName="col-span-3 gap-0 overflow-visible"
        centerClassName="hidden"
        actionsClassName="hidden"
        leading={
          <>
            <Link
              to="/dashboard"
              className="mr-4 text-sm text-white/40 transition-colors hover:text-white/80"
            >
              &larr; Back
            </Link>
            <div className="mr-4 h-4 w-px bg-white/10" />
            <span className="mr-3 text-sm font-medium text-white/80">{gameName}</span>
            {projectPlacement && (
              <span
                data-testid="workspace-project-placement"
                className="mr-2 rounded-full border border-white/10 bg-white/5 px-2 py-0.5 text-[11px] text-white/55"
                title="Project placement cannot be changed"
              >
                {projectPlacementLabel}
              </span>
            )}
            <Badge data-testid="deploy-state-pill" variant={deployBadge.variant} className="mr-2">
              {deployBadge.label}
            </Badge>
            {deployState === 'error' && failedStepName && (
              <span
                data-testid="deploy-failure-step-chip"
                className="mr-2 truncate rounded-md bg-red-500/10 px-2 py-0.5 text-xs text-red-200/90"
                title={`Failed step: ${failedStepName}`}
              >
                <span className="text-red-300/60">Failed step:</span>{' '}
                <span className="font-mono">{failedStepName}</span>
              </span>
            )}
            {deployState === 'error' && failedRunUrl && (
              <a
                href={failedRunUrl}
                target="_blank"
                rel="noreferrer"
                data-testid="deploy-failure-run-link-pill"
                className="mr-2 rounded-md border border-red-500/30 bg-red-500/5 px-2 py-0.5 text-[11px] text-red-200/80 transition-colors hover:bg-red-500/15"
                title="Open the failed run on GitHub"
              >
                View on GitHub ↗
              </a>
            )}
            {gitBranch && (
              <span className="mr-2 rounded-md bg-white/5 px-2 py-0.5 font-mono text-xs text-white/40">
                {gitBranch}
              </span>
            )}
            {bifrostPhase && (
              <span
                className={`mr-2 rounded-full px-2 py-0.5 text-xs font-medium ${PHASE_STYLE[bifrostPhase] ?? 'bg-white/10 text-white/50'}`}
              >
                {bifrostPhase}
              </span>
            )}
            {registryVersion && (
              <span className="mr-2 text-xs text-white/30">{registryVersion}</span>
            )}
            <div className="flex-1" />
            {showBackToPlanButton && onBackToPlanRequest && (
              <Button
                type="button"
                variant="secondary"
                size="sm"
                onClick={onBackToPlanRequest}
                disabled={backToPlanBusy}
                data-testid="workspace-back-to-plan-button"
                className="mr-3 border-amber-500/30 bg-amber-500/10 text-amber-100 hover:border-amber-400/50 hover:bg-amber-500/15 disabled:opacity-40"
              >
                ← Back to plan
              </Button>
            )}
            <Button
              variant="secondary"
              size="sm"
              onClick={handleOpenInCursor}
              disabled={!!editorDisabledReason}
              title={editorDisabledReason ?? 'Open in Cursor'}
              className="mr-3"
            >
              <svg
                width="12"
                height="12"
                viewBox="0 0 16 16"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M5 2L2 8l3 6M11 2l3 6-3 6"
                  stroke="currentColor"
                  strokeWidth="1.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
                <path d="M8 6v4" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
              </svg>
              Open in Cursor
            </Button>
            <Button
              variant="secondary"
              size="sm"
              gated={!gameBugReport}
              onClick={handleReportBug}
              aria-label={`Report a bug for ${gameName}`}
              title={reportBugTooltip}
              className="mr-3"
            >
              <Bug size={12} strokeWidth={1.8} />
              Report a bug
            </Button>
            {cloneDeployAccessWarning && (
              <CloneDeployAccessWarningPill warning={cloneDeployAccessWarning} />
            )}
            {onProtoHub && shellChrome === 'electron' && (
              <Button
                type="button"
                variant="secondary"
                size="sm"
                onClick={onProtoHub}
                data-testid="display-on-hub-button"
                className="mr-3 rounded-md text-white/55"
              >
                <MonitorPlay size={13} strokeWidth={1.75} />
                Display on Hub
              </Button>
            )}
            <Button
              // #2504 — `gated` centralizes the aria-disabled + guarded-onClick
              // pattern in the primitive so the click can't fire while the
              // control stays hoverable for its gating tooltip/title.
              gated={deployUiState.deployButtonDisabled}
              onClick={onDeploy}
              data-testid="deploy-to-tv-button"
              data-deploy-gated={deployUiState.deployButtonGated ? 'true' : 'false'}
              title={deployUiState.deployButtonTitle}
              className="aria-disabled:cursor-not-allowed aria-disabled:opacity-40"
            >
              {deployUiState.deployButtonLabel}
            </Button>
          </>
        }
      />
    );
  }

  return (
    <FoundryShellHeader
      chrome={shellChrome}
      className="relative z-30 border-b border-white/[0.08] pr-4"
      style={{
        background: 'var(--color-crucible-bg)',
      }}
    >
      <div className="flex min-w-0 items-center overflow-hidden">
        {/* Home button — circle icon matching the Dashboard settings button */}
        <Link
          to="/dashboard"
          className="app-region-no-drag flex h-10 w-10 shrink-0 items-center justify-center rounded-full border border-white/[0.11] transition-all hover:border-white/[0.18]"
          style={{
            background: 'var(--color-crucible-bg)',
            color: 'var(--text-3)',
            transitionTimingFunction: 'var(--ease-flora)',
            transitionDuration: 'var(--motion-fast)',
          }}
          aria-label="Back to Dashboard"
        >
          <Home size={16} strokeWidth={1.75} />
        </Link>

        {/* Vertical divider */}
        <span className="mx-3 h-4 w-px shrink-0 rounded-full bg-white/10" />

        <div className="relative min-w-0 shrink">
          {/* New UI: the title opens the project settings panel (editing now
              lives in the panel). Legacy keeps the plain title. */}
          {newUi ? (
            <>
              <Button
                ref={titleBtnRef}
                variant="ghost"
                onClick={() => setProjectPanelOpen((v) => !v)}
                data-testid="project-title-trigger"
                aria-haspopup="dialog"
                aria-expanded={projectPanelOpen}
                title="Project settings"
                className="f-title group flex min-w-0 max-w-full items-center gap-1 rounded-md px-1 py-1 hover:bg-white/[0.06] hover:text-inherit"
                style={{ color: 'var(--text-1)' }}
              >
                <span className="min-w-0 truncate">{gameName}</span>
                <ChevronDown
                  size={15}
                  strokeWidth={2}
                  className={`shrink-0 text-white/45 transition-transform ${
                    projectPanelOpen ? 'rotate-180' : ''
                  }`}
                />
              </Button>
              {projectPanelOpen && gameId && (
                <ProjectSettingsPanel
                  gameId={gameId}
                  gameName={gameName}
                  statusBadge={statusBadge}
                  version={gameStatus?.registry?.version}
                  repoUrl={repoUrl}
                  repoFullName={gameStatus?.clonedFrom}
                  inAppPairingAvailable={inAppPairingAvailable}
                  inAppPairingNextLaunch={inAppPairingNextLaunch}
                  onInAppPairingNextLaunchChange={onInAppPairingNextLaunchChange}
                  tileImageEnabled={tileImageUpload}
                  renameDisabledReason={renameDisabledReason}
                  tileEditDisabledReason={tileEditDisabledReason}
                  triggerRef={titleBtnRef}
                  onClose={() => setProjectPanelOpen(false)}
                />
              )}
            </>
          ) : (
            <span className="f-title" style={{ color: 'var(--text-1)' }}>
              {gameName}
            </span>
          )}
        </div>

        {projectPlacement && (
          <span
            data-testid="workspace-project-placement"
            className="ml-2 shrink-0 rounded-full border border-white/10 bg-white/5 px-2 py-0.5 text-[11px] text-white/55"
            title="Project placement cannot be changed"
          >
            {projectPlacementLabel}
          </span>
        )}

        {/* Issue #791 — agent-activity pill. New UI surfaces this in
            WorkspaceRightPanel instead; legacy UI keeps the header pill. */}
        {!newUi && (
          <AgentActivityPill
            snapshot={activitySnapshot ?? null}
            interviewStatus={interviewStatus}
            runState={runState}
          />
        )}

        {/* New UI: status chips grouped + muted in a single pill row.
            Legacy UI keeps main's unwrapped chip layout below. */}
        {newUi && (
          <div className="ml-3 flex min-w-0 items-center gap-1.5 overflow-hidden">
            {deployState === 'error' && failedStepName && (
              <span
                data-testid="deploy-failure-step-chip"
                className="max-w-[11rem] truncate rounded-full bg-red-500/10 px-2 py-0.5 text-[11px] text-red-200/90"
                title={`Failed step: ${failedStepName}`}
              >
                <span className="text-red-300/60">Failed:</span>{' '}
                <span className="font-mono">{failedStepName}</span>
              </span>
            )}
            {deployState === 'error' && failedRunUrl && (
              <a
                href={failedRunUrl}
                target="_blank"
                rel="noreferrer"
                data-testid="deploy-failure-run-link-pill"
                className="shrink-0 rounded-full border border-red-500/30 bg-red-500/5 px-2 py-0.5 text-[11px] text-red-200/80 transition-colors hover:bg-red-500/15"
                title="Open the failed run on GitHub"
              >
                View on GitHub ↗
              </a>
            )}
            {gitBranch && gitBranch !== 'main' && gitBranch !== 'master' && (
              <span
                className="min-w-0 max-w-[16rem] truncate font-mono text-[11px]"
                style={{ color: 'var(--text-3)' }}
                title={gitBranch}
              >
                {gitBranch}
              </span>
            )}
            {bifrostPhase && (
              <span
                className={`shrink-0 rounded-full px-2 py-0.5 text-[11px] font-medium ${PHASE_STYLE[bifrostPhase] ?? 'bg-white/10 text-white/50'}`}
                title={`TV deployment: ${bifrostPhase}`}
              >
                {bifrostPhase}
              </span>
            )}
            {/* Version moved to the project settings panel (title click). */}
          </div>
        )}

        {/* Bridge-3 — Furnace build-status indicator. Renders only when
            the engineer is genuinely on the Furnace path (in cohort +
            flag on + IPC loaded) AND a turn is in flight OR the most-
            recent terminal chunk was a Furnace failure. The component
            self-hides on `routedToFurnace=false`, so flag-OFF + out-of-
            cohort + IPC-not-loaded all render identical chrome to
            pre-Bridge-3 main. */}
        {furnaceBuildState.routedToFurnace && (
          <div className="ml-2 shrink-0">
            <FurnaceBuildIndicator state={furnaceBuildState} />
          </div>
        )}

        {/* Legacy UI chip layout — main's existing surface. Preserved
            exactly so the legacy UI is unchanged when newUi is OFF. */}
        {!newUi && (
          <>
            {/* Cloud-VM supportability TDD §A-1 — VM-status pill, mounted
                only when the cloud-VM feature flag is ON. Gate lives at this
                call site so `WorkspaceHeader` stays SSR-friendly for tests
                that render it without a QueryClientProvider; the pill itself
                still self-gates on whether a `vm:connection-state` push has
                been received before rendering anything. */}
            {cloudVmEnabled ? <VmStatusPill /> : null}

            <Badge data-testid="deploy-state-pill" variant={deployBadge.variant} className="mr-2">
              {deployBadge.label}
            </Badge>

            {deployState === 'error' && failedStepName && (
              <span
                data-testid="deploy-failure-step-chip"
                className="mr-2 truncate rounded-md bg-red-500/10 px-2 py-0.5 text-xs text-red-200/90"
                title={`Failed step: ${failedStepName}`}
              >
                <span className="text-red-300/60">Failed step:</span>{' '}
                <span className="font-mono">{failedStepName}</span>
              </span>
            )}
            {deployState === 'error' && failedRunUrl && (
              <a
                href={failedRunUrl}
                target="_blank"
                rel="noreferrer"
                data-testid="deploy-failure-run-link-pill"
                className="mr-2 rounded-md border border-red-500/30 bg-red-500/5 px-2 py-0.5 text-[11px] text-red-200/80 transition-colors hover:bg-red-500/15"
                title="Open the failed run on GitHub"
              >
                View on GitHub ↗
              </a>
            )}

            {gitBranch && (
              <span className="mr-2 rounded-md bg-white/5 px-2 py-0.5 font-mono text-xs text-white/40">
                {gitBranch}
              </span>
            )}

            {bifrostPhase && (
              <span
                className={`mr-2 rounded-full px-2 py-0.5 text-xs font-medium ${PHASE_STYLE[bifrostPhase] ?? 'bg-white/10 text-white/50'}`}
              >
                {bifrostPhase}
              </span>
            )}

            {registryVersion && (
              <span className="mr-2 text-xs text-white/30">{registryVersion}</span>
            )}
          </>
        )}
      </div>

      {/* Mode pills — a real center grid column, so long left metadata
          truncates instead of painting underneath the navigation.
          Sean, 2026-07-17: a lone "Develop" pill with nothing else to
          switch to (no Design, no Art) reads as pointless tab chrome
          when you're already on it — omit the whole bar in exactly that
          case, matching NewGame.tsx's own "nothing to switch between ->
          no tab bar" precedent. Every OTHER mode (preview/design/art/
          plan/protohub/craft) still needs it: Develop is the only way
          back to chat from the full-bleed preview surface, which has no
          exit control of its own. `selectionAddedHint` also keeps it —
          that popover is positioned relative to THIS button and has
          nothing else to anchor to. */}
      {(activeMode !== 'chat' ||
        onDesign ||
        designDisabledReason ||
        onArt ||
        artDisabledReason ||
        onPreviewTab ||
        selectionAddedHint) && (
        <div className="app-region-no-drag flex min-w-0 items-center justify-center">
          <div
            className="flex h-10 items-center gap-0.5 px-1 rounded-full"
            style={{
              background: 'var(--surface-mode-pill-bar)',
              boxShadow: 'var(--surface-mode-pill-bar-ring)',
            }}
          >
            {/* First tab. With live-preview ON it is "Develop" (wand) — the
              default side-by-side chat | preview view; the plain "Chat"
              tab is retired. With the flag OFF it falls back to the
              original full-width Chat tab. The select-to-chat "added"
              hint anchors to whichever is shown. */}
            <span className="relative inline-flex">
              {onLivePreview ? (
                <Button
                  variant="ghost"
                  data-chat-tab
                  data-testid="live-preview-pill"
                  className={`mode-pill aria-disabled:cursor-not-allowed aria-disabled:opacity-40${selectionAddedHint ? ' animate-selection-pop ring-2 ring-crucible-accent' : ''}`}
                  data-active={activeMode === 'chat' ? 'true' : 'false'}
                  data-selection-added={selectionAddedHint ? 'true' : 'false'}
                  data-disabled={!gameId ? 'true' : 'false'}
                  // #2504 — `gated` guards the click centrally; still hoverable.
                  //
                  // #4061 — this used to be `!gameId || previewForceDisabled`,
                  // which gated a NON-preview destination on preview
                  // availability. Develop is chat, build state and diagnostics;
                  // whether the live preview can start has no bearing on
                  // whether the user may look at them, and least of all when
                  // the preview being unavailable is exactly why they want to.
                  //
                  // It was a dead end, not just an annoyance: while the guided
                  // interview is running it owns the whole centre column
                  // (WorkspaceChatSurface.tsx), and clicking Preview swaps the
                  // interview out for the blocked pane (Workspace.tsx). Develop
                  // is the only way back, and it was inert in precisely that
                  // state — which is the DEFAULT state of a new game, whose
                  // deps are still installing. Two clicks, no way back, on the
                  // first thing a new user does.
                  gated={!gameId}
                  onClick={onLivePreview}
                  title={
                    !gameId
                      ? 'Open a game to develop'
                      : previewForceDisabled
                        ? // Still explain WHY the preview half is unavailable —
                          // the tab now navigates, so this is information rather
                          // than a refusal.
                          (previewDisabledReason ?? 'Develop — the live preview is not ready yet')
                        : 'Develop — chat with the agent alongside the live preview'
                  }
                >
                  {/* Only STATE renders as a glyph here — a spinner while the
                      preview boots, a lock when it's gated. The decorative
                      wand that used to sit in this slot read as "generate
                      with AI" (the meaning it carries on every other surface)
                      and made a navigation tab look like an action button. */}
                  {/* #4061 — the lock now reflects only "no game open", the one
                      state in which this tab genuinely does nothing. Showing it
                      while the preview warms up labelled a reachable
                      destination as locked, which is what made the dead end
                      look intentional rather than broken. */}
                  {previewLoading ? <PreviewSpinnerIcon /> : !gameId ? <PreviewLockIcon /> : null}
                  <span>Develop</span>
                </Button>
              ) : (
                <Button
                  variant="ghost"
                  data-chat-tab
                  className={`mode-pill${selectionAddedHint ? ' animate-selection-pop ring-2 ring-crucible-accent' : ''}`}
                  data-active={activeMode === 'chat' ? 'true' : 'false'}
                  data-selection-added={selectionAddedHint ? 'true' : 'false'}
                  onClick={onChat}
                  title="Open Build"
                >
                  <span>Build</span>
                </Button>
              )}
              {selectionAddedHint && (
                <span
                  role="status"
                  data-testid="selection-added-hint"
                  // Matches the shared Tooltip primitive's surface (neutral
                  // hairline border, not a gold accent border) so it reads like
                  // every other hovering popover.
                  className="app-region-no-drag absolute left-1/2 top-[calc(100%+8px)] z-50 w-[240px] -translate-x-1/2 rounded-[var(--radius-md)] border border-white/[0.14] bg-[#18151f] px-3 py-2 text-[12px] leading-snug text-white/85 shadow-xl"
                >
                  Your selection was added to Build. Ask the build agent to make changes.
                </span>
              )}
            </span>
            {/* Design pill — rendered whenever Design is available for the current
              local game under feature/config gates. */}
            {(onDesign || designDisabledReason) && (
              <Button
                variant="ghost"
                className={`mode-pill${activeMode === 'chat' ? ' ml-3.5' : ''}`}
                data-testid="design-mode-button"
                data-active={activeMode === 'design' ? 'true' : 'false'}
                onClick={onDesign}
                disabled={!onDesign}
                title={designDisabledReason ?? 'Open Design'}
              >
                <span>Design</span>
              </Button>
            )}
            {(onArt || artDisabledReason) && (
              <Button
                variant="ghost"
                className="mode-pill ml-3.5"
                data-testid="art-mode-button"
                data-active={activeMode === 'art' ? 'true' : 'false'}
                onClick={onArt}
                disabled={!onArt}
                title={artDisabledReason ?? 'Open Art'}
              >
                <span>Art</span>
              </Button>
            )}
            {/* Preview — #4053 — unlike Design/Art, ALWAYS clickable, even when
              blocked (no game / phase-gated / force-disabled): the click
              still fires (`onPreviewTab`) so the blocked-pane content can
              answer "why", instead of a disabled pill silently no-op'ing.
              `data-blocked`/`data-phase-gated` mirror the icon button's
              gating signal for e2e fixture guards; NOT wired through the
              shared Button's `gated` prop, which would swallow the click at
              the DOM level and defeat the "always clickable" requirement. */}
            {onPreviewTab && (
              <Button
                variant="ghost"
                className="mode-pill ml-3.5"
                data-testid="preview-mode-button"
                data-active={activeMode === 'preview' ? 'true' : 'false'}
                data-blocked={previewPill.disabled ? 'true' : 'false'}
                data-phase-gated={previewPill.phaseGated ? 'true' : 'false'}
                onClick={onPreviewTab}
                title={
                  previewPill.disabled ? previewPill.title : 'Open a larger preview for testing'
                }
              >
                {previewPill.disabled && previewPill.icon === 'lock' ? (
                  <Lock size={12} strokeWidth={1.75} />
                ) : null}
                <span>Preview</span>
              </Button>
            )}
            {/* The Weekend-app pairing toggle moved into the project settings
              panel (opened from the game title). */}
          </div>
        </div>
      )}

      <div className="flex min-w-0 items-center justify-end gap-2">
        {cloneDeployAccessWarning && (
          <CloneDeployAccessWarningPill warning={cloneDeployAccessWarning} />
        )}
        {/* Preview lifecycle controls (Stop / Play / Reload) moved into
            the preview's floating toolbar below the TV (2026-06-13). */}

        {/* The right-panel toggle was removed — the activity rail is now the
            "State" tab inside the chat panel, so there's no standalone panel
            to collapse. */}

        {/* Open in Cursor — icon-only; hover reveals the label. Opens the
            game workspace via the game:open-in-editor IPC. */}
        <span className="relative inline-flex">
          <Button
            type="button"
            variant="ghost"
            size="icon"
            onClick={handleOpenInCursor}
            disabled={!!editorDisabledReason}
            aria-label="Open in Cursor"
            data-testid="open-in-cursor-button"
            className="peer shrink-0 rounded-full border border-white/10 text-white/60 hover:border-white/20 hover:text-white/90"
          >
            <CursorGlyph />
          </Button>
          <Tooltip align="end">{editorDisabledReason ?? 'Open in Cursor'}</Tooltip>
        </span>

        <span className="relative inline-flex">
          <Button
            type="button"
            variant="ghost"
            size="icon"
            gated={!gameBugReport}
            onClick={handleReportBug}
            aria-label={`Report a bug for ${gameName}`}
            data-testid="workspace-report-bug-button"
            className="peer shrink-0 rounded-full border border-white/10 text-white/60 hover:border-white/20 hover:text-white/90"
          >
            <Bug size={15} strokeWidth={1.8} />
          </Button>
          <Tooltip align="end">{reportBugTooltip}</Tooltip>
        </span>

        {/* #4053 [AC4] — the icon-only Preview button that lived here was
            removed once the Preview mode-pill tab (above) took over
            navigation. Two affordances answering "is preview blocked, and
            why" would drift independently — the resolved product decision
            was to remove, not keep a parallel shortcut. See the Preview
            pill in the mode-pill bar for the current entry point. */}

        {onProtoHub && shellChrome === 'electron' && (
          <span className="relative inline-flex">
            <Button
              variant="ghost"
              size="icon"
              onClick={onProtoHub}
              data-testid="display-on-hub-button"
              aria-label="Display on Hub"
              className={`peer shrink-0 border ${
                activeMode === 'protohub'
                  ? 'border-cyan-300/60 text-cyan-200 hover:bg-cyan-300/10 hover:text-cyan-200'
                  : 'border-white/10 text-white/70 hover:border-white/20 hover:text-white/90'
              }`}
            >
              <MonitorPlay size={15} strokeWidth={1.8} />
            </Button>
            <Tooltip align="end">Display on Hub</Tooltip>
          </span>
        )}

        {/*
          Issue #524 — Deploy-to-TV gate. The button is disabled when the
          game isn't fully built (framework-only / partial / wrong phase),
          with the gate's reason rendered as a hover-tooltip so the user
          can see WHY they can't deploy. Falls through to the existing
          `deployState === 'deploying'` lock when a deploy is already in
          flight. Defence-in-depth alongside #519's state-recording fix.
         */}
        <span className="relative inline-flex">
          {/* #2406 tooltip-regression fix: aria-disabled + guarded onClick
              instead of native `disabled`, so the peer-hover Tooltip carrying
              the deploy-gate reason (why deploy is blocked / a deploy is in
              flight) still shows on hover. See the preview-launch note above. */}
          <Button
            // #2504 — `gated` guards the click centrally; still hoverable so
            // the peer-hover deploy-gate Tooltip below still fires.
            gated={deployUiState.deployButtonDisabled}
            onClick={onDeploy}
            data-testid="deploy-to-tv-button"
            data-deploy-gated={deployUiState.deployButtonGated ? 'true' : 'false'}
            aria-label={deployUiState.deployButtonLabel}
            className="peer py-1.5 text-sm aria-disabled:cursor-not-allowed aria-disabled:opacity-40"
          >
            <Tv size={15} strokeWidth={2} />
            {deployUiState.deployButtonLabel}
          </Button>
          <Tooltip align="end">{deployUiState.deployButtonTitle}</Tooltip>
        </span>
      </div>
    </FoundryShellHeader>
  );
}

// Cursor app icon — the isometric cube mark, rendered in three tonal faces
// via currentColor opacities so it reads as the Cursor logo at chip size.
function CursorGlyph() {
  return (
    <svg width="15" height="15" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path d="M12 2.5 20.5 7.25 12 12 3.5 7.25Z" fill="currentColor" fillOpacity="0.55" />
      <path d="M3.5 7.25 12 12 12 21.5 3.5 16.75Z" fill="currentColor" fillOpacity="0.95" />
      <path d="M20.5 7.25 12 12 12 21.5 20.5 16.75Z" fill="currentColor" fillOpacity="0.3" />
      <path
        d="M12 2.5 20.5 7.25V16.75L12 21.5 3.5 16.75V7.25Z"
        stroke="currentColor"
        strokeOpacity="0.5"
        strokeWidth="0.9"
        strokeLinejoin="round"
      />
    </svg>
  );
}

function PreviewSpinnerIcon() {
  return (
    <svg
      width="13"
      height="13"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2.5"
      strokeLinecap="round"
      className="animate-spin"
      aria-hidden="true"
    >
      <path d="M12 2a10 10 0 0 1 10 10" />
    </svg>
  );
}

function PreviewLockIcon() {
  return <Lock size={13} strokeWidth={1.75} />;
}

function CloneDeployAccessWarningPill({
  warning,
}: {
  warning: CloneDeployAccessWarning;
}): React.JSX.Element {
  const title =
    warning.state === 'read-only'
      ? `You can edit this local copy, but deploy/publish cannot push to ${warning.repoFullName} without write access.`
      : `You can edit this local copy, but Foundry could not verify write access to ${warning.repoFullName}.`;
  return (
    <div
      data-testid="clone-deploy-access-warning"
      className="app-region-no-drag flex max-w-[300px] items-center gap-1.5 rounded-full border border-amber-400/25 bg-amber-500/10 px-2.5 py-1 text-[11px] text-amber-100/90"
      title={`${title}${warning.reason ? ` ${warning.reason}` : ''}`}
    >
      <AlertTriangle size={12} strokeWidth={1.8} className="shrink-0 text-amber-300" />
      <span className="truncate">
        {warning.state === 'read-only' ? 'Source repo read-only' : 'Source repo access unknown'}
      </span>
      <Button
        type="button"
        variant="ghost"
        size="icon"
        onClick={() => window.open(warning.repoUrl, '_blank', 'noopener,noreferrer')}
        className="h-auto w-auto p-0 text-amber-100/75 hover:text-amber-50"
        aria-label={`Open ${warning.repoFullName} on GitHub`}
      >
        <ExternalLink size={11} strokeWidth={1.8} />
      </Button>
    </div>
  );
}

const GAME_STATUS_PILL: Record<GameStatusBadge, { label: string; dot: string; text: string }> = {
  draft: { label: 'Draft', dot: 'bg-white/40', text: 'text-white/60' },
  building: { label: 'Building', dot: 'bg-info', text: 'text-info' },
  prototype: { label: 'Prototype', dot: 'bg-warning', text: 'text-warning' },
  live: { label: 'Live', dot: 'bg-success', text: 'text-success' },
  error: { label: 'Error', dot: 'bg-error', text: 'text-error' },
};

/**
 * Game tile image section (behind the `tileImageUpload` flag). Shows the
 * current tile, with hover/drag-and-drop/click to upload a replacement, plus a
 * caption nudging the user to chat with the agent to generate one.
 */
function GameTileImageField({
  gameId,
  disabledReason,
}: {
  gameId: string;
  /** #3145 — when set, the dropzone is gated (cloud placement has no
   *  `game.set-tile-image@1` handler). Shows the reason instead of the raw
   *  wire error and never attempts the upload invoke. */
  disabledReason?: string;
}): React.JSX.Element {
  const client = useCrucibleClient();
  const [dragOver, setDragOver] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [overrideUrl, setOverrideUrl] = useState<string | null>(null);
  const [uploadError, setUploadError] = useState<string | null>(null);
  const fileRef = useRef<HTMLInputElement>(null);
  const { data: tile } = useIpcQuery('game:get-tile-image', { gameId });
  const imageUrl = overrideUrl ?? tile?.assetUrl ?? null;

  const handleFile = useCallback(
    async (file: File | undefined | null): Promise<void> => {
      // #3145 — never attempt the missing cloud method. The gate reason is
      // surfaced by the render precedence below (disabledReason wins over any
      // stale uploadError), so just bail without touching error state.
      if (disabledReason) return;
      if (!file) return;
      if (uploading) return;
      if (!isSupportedTileUploadFile(file)) {
        setUploadError('That file is not supported - pick a PNG, JPG, or WebP.');
        return;
      }
      setUploading(true);
      setUploadError(null);
      try {
        const upload = await prepareTileImageUpload(file);
        const result = await client.invoke('game:set-tile-image', {
          gameId,
          ...upload,
        });
        // Cache-bust so the <img> reloads the freshly-written file.
        setOverrideUrl(`${result.assetUrl}?v=${Date.now()}`);
      } catch (err) {
        // Peel off Electron's wire-format `[CRUCIBLE_ERR_V1]` envelope so the
        // console logs the real message (not the encoded payload) and the
        // user gets a readable, non-silent failure.
        const decoded = unflattenIpcError(err);
        console.error('[tile-image] upload failed', decoded);
        setUploadError(decoded.message || 'Upload failed - please try again.');
      } finally {
        setUploading(false);
      }
    },
    [gameId, uploading, disabledReason],
  );

  const disabled = !!disabledReason;
  // The message shown beneath the dropzone. When the affordance is gated
  // (cloud placement) the gate reason WINS over any stale upload error — a
  // raw wire failure captured in the brief window before capability gating
  // resolved must not linger over the human gate reason (#3165, Bugbot). In
  // the ungated (local) case disabledReason is null, so a genuine uploadError
  // still shows.
  const message = disabledReason ?? uploadError ?? null;

  return (
    <div className="pb-3">
      <p
        className="mb-1 text-[10px] font-medium uppercase tracking-[0.08em]"
        style={{ color: 'var(--text-3)' }}
      >
        Game tile image
      </p>
      <div
        role="button"
        tabIndex={disabled ? -1 : 0}
        aria-disabled={disabled || undefined}
        onClick={() => {
          if (disabled) return;
          fileRef.current?.click();
        }}
        onKeyDown={(e) => {
          if (disabled) return;
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            fileRef.current?.click();
          }
        }}
        onDragOver={(e) => {
          if (disabled) return;
          e.preventDefault();
          setDragOver(true);
        }}
        onDragLeave={() => setDragOver(false)}
        onDrop={(e) => {
          e.preventDefault();
          setDragOver(false);
          if (disabled) return;
          void handleFile(e.dataTransfer.files?.[0]);
        }}
        data-testid="game-tile-image-dropzone"
        className={`group relative flex aspect-video w-full items-center justify-center overflow-hidden rounded-lg border bg-white/[0.04] transition-colors ${
          disabled
            ? 'cursor-not-allowed border-white/10 opacity-50'
            : dragOver
              ? 'cursor-pointer border-crucible-accent'
              : 'cursor-pointer border-white/10 hover:border-white/20'
        }`}
      >
        {imageUrl ? (
          <img
            src={imageUrl}
            alt="Game tile"
            className="h-full w-full object-cover"
            draggable={false}
          />
        ) : (
          <span className="text-[11px]" style={{ color: 'var(--text-3)' }}>
            No tile image yet
          </span>
        )}
        {!disabled && (
          <div
            className={`absolute inset-0 flex items-center justify-center bg-black/55 text-[12px] font-medium text-white/90 transition-opacity ${
              dragOver || uploading ? 'opacity-100' : 'opacity-0 group-hover:opacity-100'
            }`}
          >
            {uploading
              ? 'Uploading…'
              : dragOver
                ? 'Drop to upload'
                : imageUrl
                  ? 'Replace'
                  : 'Upload'}
          </div>
        )}
        <input
          ref={fileRef}
          type="file"
          accept="image/png,image/jpeg,image/webp,.png,.jpg,.jpeg,.webp"
          className="hidden"
          onChange={(e) => {
            void handleFile(e.currentTarget.files?.[0]);
            e.currentTarget.value = '';
          }}
        />
      </div>
      {!disabled && (
        <p className="mt-1.5 text-[11px] leading-snug" style={{ color: 'var(--text-3)' }}>
          Upload an image, or ask the agent in chat to generate a new game tile image.
        </p>
      )}
      {message && (
        <p
          role="alert"
          data-testid="game-tile-image-error"
          className="mt-1 text-[11px] leading-snug text-error"
        >
          {message}
        </p>
      )}
    </div>
  );
}

/**
 * Project settings popover — opened by clicking the game title in the editor
 * top bar. Holds the editable title, the immutable project name + GitHub repo
 * link, version, the lifecycle status, and the Weekend-app toggle. Dismisses
 * on Escape, on a click outside (excluding the title trigger), or by clicking
 * the title again (handled by the trigger's own toggle).
 */
function ProjectSettingsPanel({
  gameId,
  gameName,
  statusBadge,
  version,
  repoUrl,
  repoFullName,
  inAppPairingAvailable,
  inAppPairingNextLaunch,
  onInAppPairingNextLaunchChange,
  tileImageEnabled,
  renameDisabledReason,
  tileEditDisabledReason,
  triggerRef,
  onClose,
}: {
  gameId: string;
  gameName: string;
  statusBadge: GameStatusBadge;
  version?: string;
  repoUrl?: string;
  repoFullName?: string;
  inAppPairingAvailable: boolean;
  inAppPairingNextLaunch: boolean;
  onInAppPairingNextLaunchChange?: (value: boolean) => void;
  tileImageEnabled?: boolean;
  /** #3145 — gated tile-upload reason (cloud placement lacks the handler). */
  tileEditDisabledReason?: string;
  /** #3144 — gated rename reason (cloud placement lacks the handler). */
  renameDisabledReason?: string;
  triggerRef: React.RefObject<HTMLElement | null>;
  onClose: () => void;
}): React.JSX.Element {
  const panelRef = useRef<HTMLDivElement>(null);
  // The header's leading cell is `overflow-hidden`, so an absolutely-positioned
  // popover would be clipped. Render it `fixed`, anchored to the trigger's rect
  // (no transformed ancestor, so fixed escapes the clip).
  const [pos, setPos] = useState<{ left: number; top: number } | null>(null);
  useLayoutEffect(() => {
    const update = (): void => {
      const r = triggerRef.current?.getBoundingClientRect();
      if (r) setPos({ left: r.left, top: r.bottom + 8 });
    };
    update();
    window.addEventListener('resize', update);
    return () => window.removeEventListener('resize', update);
  }, [triggerRef]);
  useEffect(() => {
    const onKey = (e: KeyboardEvent): void => {
      if (e.key === 'Escape') onClose();
    };
    const onDown = (e: MouseEvent): void => {
      const target = e.target as Node;
      // Ignore clicks on the title trigger — it toggles the panel itself.
      if (panelRef.current?.contains(target) || triggerRef.current?.contains(target)) return;
      onClose();
    };
    document.addEventListener('keydown', onKey);
    document.addEventListener('mousedown', onDown);
    return () => {
      document.removeEventListener('keydown', onKey);
      document.removeEventListener('mousedown', onDown);
    };
  }, [onClose, triggerRef]);

  const pill = GAME_STATUS_PILL[statusBadge];
  // Each section is divided by a hairline rule (first has no top border).
  const Field = ({
    label,
    first,
    children,
  }: {
    label: string;
    first?: boolean;
    children: React.ReactNode;
  }): React.JSX.Element => (
    <div className={first ? 'pb-3' : 'border-t border-white/[0.08] py-3'}>
      <p
        className="mb-1 text-[10px] font-medium uppercase tracking-[0.08em]"
        style={{ color: 'var(--text-3)' }}
      >
        {label}
      </p>
      {children}
    </div>
  );

  return (
    <div
      ref={panelRef}
      data-testid="project-settings-panel"
      role="dialog"
      aria-label="Project settings"
      className="app-region-no-drag fixed z-50 w-80 rounded-xl border border-white/10 p-4"
      style={{
        left: pos?.left ?? 0,
        top: pos?.top ?? 0,
        visibility: pos ? 'visible' : 'hidden',
        background: 'var(--surface-card)',
        boxShadow: 'var(--shadow-modal)',
      }}
    >
      {tileImageEnabled && (
        <GameTileImageField gameId={gameId} disabledReason={tileEditDisabledReason} />
      )}

      <Field label="Game title" first={!tileImageEnabled}>
        <EditableGameTitle
          gameId={gameId}
          gameName={gameName}
          disabledReason={renameDisabledReason}
        />
      </Field>

      <Field label="Project name">
        <div className="flex items-center justify-between gap-2">
          <span
            className="min-w-0 truncate font-mono text-[13px]"
            style={{ color: 'var(--text-2)' }}
            title={gameId}
          >
            {gameId}
          </span>
          {repoUrl && (
            <Button
              type="button"
              variant="secondary"
              size="sm"
              onClick={() => window.open(repoUrl, '_blank', 'noopener,noreferrer')}
              aria-label={`Open ${repoFullName ?? 'the repository'} on GitHub`}
              title="Open the GitHub repo in your browser"
            >
              <Github size={12} strokeWidth={2} />
              Repo
            </Button>
          )}
        </div>
        {version && (
          <p className="mt-1 text-[11px]" style={{ color: 'var(--text-3)' }}>
            v{version}
          </p>
        )}
      </Field>

      <Field label="Status">
        <span
          data-testid="project-status-indicator"
          className="inline-flex items-center gap-1.5 text-[13px]"
        >
          <span className={`h-2 w-2 shrink-0 rounded-full ${pill.dot}`} aria-hidden />
          <span className={pill.text}>{pill.label}</span>
        </span>
      </Field>

      {inAppPairingAvailable && (
        <label className="flex cursor-pointer items-center gap-2 border-t border-white/[0.08] pt-3 text-[12px] text-white/70">
          <input
            type="checkbox"
            className="h-3.5 w-3.5 accent-sky-400"
            checked={inAppPairingNextLaunch}
            onChange={(event) => onInAppPairingNextLaunchChange?.(event.currentTarget.checked)}
          />
          <span>Weekend app (next Preview launch)</span>
        </label>
      )}
    </div>
  );
}

function EditableGameTitle({
  gameId,
  gameName,
  disabledReason,
}: {
  gameId: string;
  gameName: string;
  /** #3144 — when set, the rename affordance is gated (cloud placement has no
   *  `game.set-display-name@1` handler). The button is disabled, editing never
   *  opens, and the reason is surfaced via the rename error span. */
  disabledReason?: string;
}): React.JSX.Element {
  const client = useCrucibleClient();
  const queryClient = useQueryClient();
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState('');
  const [renameError, setRenameError] = useState<string | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  const startEdit = (): void => {
    // #3144 — gated placement: never enter edit mode (the invoke would reject).
    if (disabledReason) return;
    setDraft(gameName);
    setRenameError(null);
    setEditing(true);
  };

  useEffect(() => {
    if (editing) inputRef.current?.select();
  }, [editing]);

  const commit = async (): Promise<void> => {
    const trimmed = draft.trim();
    setEditing(false);
    // #3144 — belt-and-braces: never attempt the missing cloud method.
    if (disabledReason) return;
    if (!trimmed || trimmed === gameName) return;
    try {
      await client.invoke('game:set-display-name', { gameId, displayName: trimmed });
      setRenameError(null);
      void queryClient.invalidateQueries({
        queryKey: projectQueryKey(client, ['game:status', gameId]),
      });
      void queryClient.invalidateQueries({ queryKey: ['game:list:lite'] });
    } catch (err) {
      // Defence-in-depth (#3144): the decode must never throw out of this
      // catch — `commit` is invoked via an un-awaited `void commit()`, so a
      // throw here would become an unhandled rejection and swallow the
      // failure silently. Fall back to the raw message if decode ever throws.
      let message = 'Rename failed';
      try {
        message = unflattenIpcError(err).message || message;
      } catch {
        message = err instanceof Error ? err.message : String(err);
      }
      setRenameError(message);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>): void => {
    if (e.key === 'Enter') {
      e.preventDefault();
      void commit();
    }
    if (e.key === 'Escape') {
      setEditing(false);
    }
  };

  if (editing) {
    return (
      <input
        ref={inputRef}
        value={draft}
        onChange={(e) => setDraft(e.target.value)}
        onBlur={() => {
          void commit();
        }}
        onKeyDown={handleKeyDown}
        className="app-region-no-drag f-title w-48 rounded bg-white/[0.06] px-1 outline-none ring-1 ring-white/20 focus:ring-crucible-accent"
        style={{ color: 'var(--text-1)' }}
        data-testid="game-title-input"
      />
    );
  }

  // The message shown beneath the title. When the affordance is gated (cloud
  // placement) the gate reason WINS over any stale rename error — a raw wire
  // failure captured in the brief window before capability gating resolved
  // must not linger over the human gate reason (#3165, Bugbot). In the
  // ungated (local) case disabledReason is null, so a genuine renameError
  // still shows.
  const message = disabledReason ?? renameError ?? null;

  return (
    <div className="flex min-w-0 flex-col">
      <Button
        variant="ghost"
        onClick={startEdit}
        disabled={!!disabledReason}
        title={disabledReason ?? 'Click to rename'}
        className="f-title group flex min-w-0 max-w-full items-center justify-start gap-1.5 rounded-md px-1 py-1 hover:bg-white/[0.06] hover:text-inherit"
        style={{ color: 'var(--text-1)' }}
        data-testid="game-title-edit-btn"
      >
        <span className="min-w-0 truncate">{gameName}</span>
        {!disabledReason && (
          <Pencil
            size={11}
            strokeWidth={1.75}
            // Always visible (not hover-only) so it's clear the title is editable.
            className="shrink-0 opacity-40 transition-opacity group-hover:opacity-70"
            aria-hidden
          />
        )}
      </Button>
      {message && (
        <span className="px-1 text-[11px] text-error" data-testid="game-title-rename-error">
          {message}
        </span>
      )}
    </div>
  );
}
